/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the component tree,
 * logs the error, and displays a fallback UI.
 *
 * Error reporting is extensible via the onError callback prop,
 * which can be used to integrate with Sentry, LogRocket, or other services.
 *
 * See React docs: https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from 'scheduler-ui';

/**
 * Error report context for tracking services.
 */
export interface ErrorReportContext {
  error: Error;
  errorInfo: ErrorInfo;
  componentStack: string | null;
  timestamp: string;
  url: string;
  userAgent: string;
}

/**
 * Callback type for error reporting integration.
 * Can be used to send errors to Sentry, LogRocket, or custom services.
 */
export type ErrorReportCallback = (context: ErrorReportContext) => void;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Optional callback for external error reporting (Sentry, etc.) */
  onError?: ErrorReportCallback;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
}

/**
 * Generate a short error ID for user reference.
 */
function generateErrorId(): string {
  return crypto.randomUUID().substring(0, 8).toUpperCase();
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, errorId: generateErrorId() };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const errorContext: ErrorReportContext = {
      error,
      errorInfo,
      componentStack: errorInfo.componentStack ?? null,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Log error to console with structured context
    console.error('ErrorBoundary caught an error:', {
      errorId: this.state.errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Call external error reporting callback if provided
    if (this.props.onError) {
      try {
        this.props.onError(errorContext);
      } catch (reportingError) {
        console.error('Error reporting callback failed:', reportingError);
      }
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-4">
              We're sorry, but something unexpected happened. Please try again.
            </p>
            {this.state.errorId && (
              <p className="text-xs text-gray-400 mb-4">
                Error reference: {this.state.errorId}
              </p>
            )}
            {this.state.error && (
              <details className="mb-6 text-left">
                <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                  Technical details
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 rounded-lg text-xs text-gray-700 overflow-auto">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <Button onClick={this.handleRetry} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
